import { useState } from 'react'
import { Money } from '@klear/money'
import { PAAS_SETTLEMENT_CURRENCY } from '@klear/paas/constants'
import { Button } from '@klear/ui/components/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@klear/ui/components/dialog'
import { MoneyInput } from '@klear/ui/components/money-input'
import { usePaasMutations, type PaasPositionRow } from '@klear/ui/hooks/use-paas'

export default function PaasFeeTermsDialog({
  position,
  onOpenChange
}: {
  position: PaasPositionRow
  onOpenChange: (open: boolean) => void
}) {
  const { setExpectedFee, isSavingExpectedFee } = usePaasMutations()
  const [feeAmount, setFeeAmount] = useState<Money>(position.expectedFee ?? Money.zero(PAAS_SETTLEMENT_CURRENCY))

  const onSubmit = async () => {
    await setExpectedFee({ supplierPaymentId: position.supplierPayment.id, feeAmount: feeAmount.toJSON() })
    onOpenChange(false)
  }

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle>Fee terms for {position.idDisplay}</DialogTitle>
          <DialogDescription>
            {position.hasFeeTerms
              ? `Currently ${position.expectedFee?.toString()}, of which ${position.feesCollected.toString()} has been collected. Replacing it re-bases what is still due.`
              : `No terms are recorded, so this position will close on principal alone and no fee can be collected against it.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <MoneyInput
            label={`Expected fee (${PAAS_SETTLEMENT_CURRENCY})`}
            value={feeAmount}
            onChange={setFeeAmount}
            currencyDisabled
            helperText={
              `Always ${PAAS_SETTLEMENT_CURRENCY} — the fee is pulled to Klear Inc like every other fee, whatever the ` +
              `payable is denominated in. Zero is valid and means the fee is contractually nothing, which is ` +
              `different from leaving it unrecorded.`
            }
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSavingExpectedFee}>
            Cancel
          </Button>
          <Button onClick={onSubmit} disabled={isSavingExpectedFee}>
            {isSavingExpectedFee ? 'Saving…' : 'Save fee terms'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
